/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TableDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Modal,
  Box,
  Typography,
  Paper,
  IconButton,

} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";

import { HandleApiActions, legalRequirements } from "../../redux/actions";
import { useDispatch } from "react-redux";
import CustomFormTextFieldV3 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV3";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
function TableDialog({
  openDialogTable,
  setOpenDialogTable,
  selectedLSDetails,
  setSelectedLSTableDetails,
  selectedLSTable,
  refreshLegalStandards,
  fetchTotalTableDetails
}) {
  const dispatch = useDispatch();
  //useState
  const [table, setTable] = useState({
    title: "",
    description: "",
  });

  const [columns, setColumns] = useState({
    column_key: "",
    column_label: "",
    column_type: "",
  });

  const [showColumnForm, setShowColumnForm] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [columnDetails, setColumnDetails] = useState([]);

  const handleCloseDialog = () => {
    setOpenDialogTable(false);
  };
  const editColumn = (req) => {
    setColumns(req);
    setEditingColumn(req.id);
    setShowColumnForm(true);
  };
  const addColumn = () => {
    if (isColumnComplete) {
      const newRequirement = {
        ...columns,
        id: editingColumn || Date.now().toString(),
      };

      if (editingColumn) {
        setColumnDetails((prev) =>
          prev.map((req) => (req.id === editingColumn ? newRequirement : req)),
        );
        setEditingColumn(null);
      } else {
        setColumnDetails((prev) => [...prev, newRequirement]);
      }

      setColumns({
        column_key: "",
        column_label: "",
        column_type: "",
      });
      setShowColumnForm(false);
    }
  };
  const deleteColumn = (id) => {
    setColumnDetails((prev) => prev.filter((req) => req.id !== id));
  };

  const isTableComplete =
    !!table?.title?.trim() && !!table?.description?.trim();

  const isColumnComplete = Object.entries(columns)
    .filter(([key]) => key !== "id")
    .every(([_, value]) => {
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return value !== null && value !== undefined;
    });

  const handleSubmit = async () => {
    const reqObj = {
      ls_id: selectedLSDetails,
      table: table,
      columns: columnDetails.map(({ id, ...rest }) => rest),
    };
    const resp = await dispatch(
      HandleApiActions({
        ...legalRequirements.add_legal_standard_table,
        params: reqObj,
        show_toast: true,
      }),
    );
    if (!resp?.error) {
      await refreshLegalStandards(false);
      await fetchTotalTableDetails()

      const resp = await dispatch(
        HandleApiActions({
          ...legalRequirements.get_legal_standard_table_details,
          params: {
            ls_id: selectedLSDetails,
            table_id: selectedLSTable,
          },
        }),
      );
      if (resp?.data?.length > 0) {
        setSelectedLSTableDetails(resp?.data[0]?.legal_standard_table || null);
      }
      handleCloseDialog();
    }
  };

  return (
    <Modal open={openDialogTable} onClose={handleCloseDialog}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1300,
          p: 2,
        }}
      >
        <Paper
          elevation={24}
          sx={{
            borderRadius: 3,
            width: "100%",
            maxWidth: "600px",
            maxHeight: "90vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <Typography variant="h6">Add Table</Typography>
            <IconButton
              onClick={handleCloseDialog}
              sx={{ "&:hover": { color: "#E5E7EB" } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Scrollable Body Content */}
          <Box
            sx={{
              p: 3,
              overflowY: "auto",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {/* Table Info */}
            <Box>
              <Box>
                <CustomFormTextFieldV3
                  label="Table Title"
                  size="small"
                  required
                  type="text"
                  fldType="text"
                  value={table.title}
                  setValue={(val) =>
                    setTable((prev) => ({ ...prev, title: val }))
                  }
                  placeholder="Enter Table Title"
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <CustomFormTextFieldV3
                  isMultiline={true}
                  label="Table Description"
                  size="small"
                  required
                  type="text"
                  fldType="text"
                  value={table.description}
                  setValue={(val) =>
                    setTable((prev) => ({ ...prev, description: val }))
                  }
                  placeholder="Enter Table Description"
                />
              </Box>
            </Box>
          </Box>

          {/* Footer Action */}
          <Box
            sx={{
              bgcolor: "#fff",
              px: 4,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <CustomButtonV2
              btnName="Add"
              variant="contained"
              handleOnClick={handleSubmit}
              disabled={!isTableComplete}
            >
              Add
            </CustomButtonV2>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
}

export default TableDialog;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};

const FieldRow = ({ label, children }) => (
  <Box display="flex" flexDirection="column" gap={1}>
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 600, color: "text.primary" }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);
